/**
 * Drop-in replacement for wagmi's `useWaitForTransactionReceipt`.
 *
 * Returns the identical shape — `{ data, isLoading, isSuccess, isError, error }`
 * — so screens keep their existing destructuring and JSX.
 *
 * The whole reload story lives here. After a refresh the component's `hash`
 * state is gone, so today's `useWaitForTransactionReceipt({ hash: undefined })`
 * simply sits idle and the flow appears never to have happened. Here the hash
 * comes from the journal instead, wagmi resumes polling for it, and the screen
 * lands back on the right state. This generalises the `nonceHash || directHash`
 * trick already used in Collateral.jsx, and makes it survive a reload.
 */

import { useCallback, useEffect, useMemo, useRef, useSyncExternalStore } from "react";
import { useAccount, useChainId, useWaitForTransactionReceipt } from "wagmi";
import * as journal from "./journal";
import { entryKey } from "./keys";
import { EVENTS, isInFlight, isSettled, toReceiptShape } from "./machine";

export function usePersistentWaitForTransactionReceipt(options = {}) {
  const { flowId, stepId, instanceId, hash, resumeSettled = false, ...wagmiOptions } =
    options;

  const { address } = useAccount();
  const chainId = useChainId();

  const key = useMemo(
    () =>
      address && flowId && stepId
        ? entryKey({ chainId, address, flowId, stepId, instanceId })
        : null,
    [chainId, address, flowId, stepId, instanceId]
  );

  const entry = useSyncExternalStore(
    journal.subscribe,
    useCallback(() => (key ? journal.getEntry(key) : null), [key]),
    useCallback(() => (key ? journal.getEntry(key) : null), [key])
  );

  /**
   * Was this row ALREADY finished when this component mounted?
   *
   * If so the component never witnessed the transition, so the outcome is
   * history, not news — and must not be projected. Reporting it anyway is what
   * reopened the "Funds are UnStaked successfully" modal on every navigation
   * back to the Stake page: that screen does
   * `if (isUnstakeConfirmed) setUnStakeSuccess(true)`, and on mount we handed it
   * a `true` left over from a transaction the user had already watched finish.
   *
   * Deliberately per-MOUNT, not per-page-load: in an SPA, navigating away and
   * back remounts without reloading, which is exactly the reported case.
   *
   * A row still IN FLIGHT at mount is not suppressed — that is the resume case
   * this engine exists for. Leave mid-transaction, come back, and the spinner is
   * restored and turns into success when the receipt lands.
   *
   * `resumeSettled` OPTS OUT of the suppression, per flow.
   *
   * Some steps exist precisely so a settled outcome can be recovered later. The
   * dispute and appeal fees are the case that forced this: the fee is taken by
   * the router, and the ONLY local trace that it was paid is this journal row.
   * Suppressing it at mount is what made the app ask for the fee a second time
   * after the user navigated away — and on the appeal path `openAppeal` has no
   * re-entry guard, so that second prompt really does take another 10 DAI.
   *
   * It is opt-in per call site and must stay that way. The default protects the
   * Stake screen, which does `if (isUnstakeConfirmed) setUnStakeSuccess(true)`
   * and would reopen its success modal on every navigation. Turning this on
   * globally would reintroduce that bug and re-fire post-confirmation effects
   * across the app.
   */
  const mountRef = useRef({ key: undefined, staleAtMount: false });
  if (mountRef.current.key !== key) {
    mountRef.current = {
      key,
      staleAtMount: !resumeSettled && !!(entry && isSettled(entry.status)),
    };
  }
  // Suppression lifts the moment the row goes back IN FLIGHT: from then on this
  // mount is watching the transaction itself, so its outcome is news again.
  // Without this the flag was one-shot per mount — a second unstake on a screen
  // you had navigated back to would confirm on chain and never report success,
  // leaving the modal and the refetch to never fire at all.
  if (mountRef.current.staleAtMount && entry && isInFlight(entry.status)) {
    mountRef.current.staleAtMount = false;
  }

  const staleAtMount = mountRef.current.staleAtMount;

  // Journal first: after a reload it is the only place the hash still exists.
  const journalHash =
    entry && entry.hashes && entry.hashes.length
      ? entry.hashes[entry.hashes.length - 1]
      : null;
  const effectiveHash = staleAtMount ? hash : journalHash || hash;

  const wagmiResult = useWaitForTransactionReceipt({
    ...wagmiOptions,
    hash: effectiveHash,
  });

  // Mirror wagmi's resolution into the journal. `transition` ignores duplicates,
  // so the recovery scanner observing the same receipt is harmless.
  useEffect(() => {
    if (!key) return;
    if (wagmiResult.isSuccess && wagmiResult.data) {
      journal.apply(key, EVENTS.RECEIPT, { receipt: wagmiResult.data });
    }
  }, [key, wagmiResult.isSuccess, wagmiResult.data]);

  useEffect(() => {
    if (!key) return;
    // A receipt with status "reverted" surfaces through wagmi as an error on
    // some versions and as data on others; handle the error shape too.
    if (wagmiResult.isError && wagmiResult.error?.receipt) {
      journal.apply(key, EVENTS.RECEIPT, { receipt: wagmiResult.error.receipt });
    }
  }, [key, wagmiResult.isError, wagmiResult.error]);

  // Stale row: behave exactly like plain wagmi did before this engine existed.
  if (!key || !entry || staleAtMount) return wagmiResult;

  const projected = toReceiptShape(entry);
  return {
    ...wagmiResult,
    // Prefer the live receipt object when we have it — it is richer than the
    // trimmed copy we persist — but let the journal drive the status flags.
    data: wagmiResult.data || projected.data,
    isLoading: projected.isLoading,
    isSuccess: projected.isSuccess,
    isError: projected.isError,
    error: projected.error || wagmiResult.error,
    txEntry: entry,
  };
}

export default usePersistentWaitForTransactionReceipt;
